import React, { useEffect, useState } from 'react'

import { FormControl, makeStyles, TextField } from '@material-ui/core'
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import axios from 'axios'
import moment from 'moment'
import { toast } from 'react-toastify'

import Loader from 'components/atoms/Loader'
import { ReactComponent as CloseIcon } from 'images/icons/circle-close.svg'
import { auth } from 'services/firebase'
import useGetLawyerStripeAccountId from 'services/firebase/hooks/lawyers/useGetLawyerStripeAccountId'
import { expertise } from 'utils/constants'
import { firebaseBaseURL } from 'utils/constants'
import { formatter } from 'utils/functions'

import './StripeCheckoutForm.scss'
const useStyles = makeStyles((theme) => ({
  formControl: {
    backgroundColor: 'transparent',
    margin: theme.spacing(1, 0),
    minWidth: 'unset',
    border: 'none',
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  field: {
    backgroundColor: 'transparent',
    color: 'rgba(0, 0, 0, 0.54)',
    fontFamily: 'Poppins',
    fontSize: 16,
    fontWeight: 400,
    '&:focus': {
      backgroundColor: '#f6fcff',
    },
  },
  select: {
    background: 'transparent',
  },
  button: {
    padding: theme.spacing(2),
    fontSize: 18,
    fontWeight: 600,
    fontFamily: 'Poppins',
    textTransform: 'uppercase',
    borderRadius: 0,
    outline: 'none',
    zIndex: 1000,
  },
}))

// const stripePromise = loadStripe('pk_test_0yoz51c9a53vOICMaLBurAVg00HREnDG8k') // dev
// const stripePromise = loadStripe('pk_test_BnrqXqzoQpp9PcRGLzbTUwJT00R1G7qw8O') // lawyerup account
const stripePromise =
  process.env.REACT_APP_ENV === 'development'
    ? loadStripe('pk_test_BnrqXqzoQpp9PcRGLzbTUwJT00R1G7qw8O')
    : loadStripe('pk_live_0eDSCMCNTmEebGC2NStKnYPK00QrtHlo1C')

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc: 'https://fonts.googleapis.com/css?family=Roboto',
    },
  ],
}

const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#666',
      color: '#222',
      fontWeight: 500,
      fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: '#999',
      },
      '::placeholder': {
        color: '#999',
      },
    },
    invalid: {
      iconColor: '#f9593a',
      color: '#f9593a',
    },
  },
}

const calculateCCAfterFee = (amount) => {
  const FIXED_FEE = 0.3
  const PERCENTAGE_FEE = 0.029
  return Number((amount + FIXED_FEE) / (1 - PERCENTAGE_FEE)).toFixed(1)
}

const SubmitButton = ({ processing, children, disabled }) => (
  <button
    className='a-modal__actions--submitButton'
    type='submit'
    disabled={processing || disabled}
  >
    {processing ? 'Processing...' : children}
  </button>
)

const ResetButton = ({ onClick }) => (
  <button type='button' className='ResetButton' onClick={onClick}>
    <svg width='32px' height='32px' viewBox='0 0 32 32'>
      <path
        fill='#FFF'
        d='M15,7.05492878 C10.5000495,7.55237307 7,11.3674463 7,16 C7,20.9705627 11.0294373,25 16,25 C20.9705627,25 25,20.9705627 25,16 C25,15.3627484 24.4834055,14.8461538 23.8461538,14.8461538 C23.2089022,14.8461538 22.6923077,15.3627484 22.6923077,16 C22.6923077,19.6960595 19.6960595,22.6923077 16,22.6923077 C12.3039405,22.6923077 9.30769231,19.6960595 9.30769231,16 C9.30769231,12.3039405 12.3039405,9.30769231 16,9.30769231 L16,12.0841673 C16,12.1800431 16.0275652,12.2738974 16.0794108,12.354546 C16.2287368,12.5868311 16.5380938,12.6540826 16.7703788,12.5047565 L22.3457501,8.92058924 L22.3457501,8.92058924 C22.4060014,8.88185624 22.4572275,8.83063012 22.4959605,8.7703788 C22.6452866,8.53809377 22.5780351,8.22873685 22.3457501,8.07941076 L22.3457501,8.07941076 L16.7703788,4.49524351 C16.6897301,4.44339794 16.5958758,4.41583275 16.5,4.41583275 C16.2238576,4.41583275 16,4.63969037 16,4.91583275 L16,7 L15,7 L15,7.05492878 Z M16,32 C7.163444,32 0,24.836556 0,16 C0,7.163444 7.163444,0 16,0 C24.836556,0 32,7.163444 32,16 C32,24.836556 24.836556,32 16,32 Z'
      />
    </svg>
  </button>
)

const CheckoutForm = ({
  offer,
  offerId,
  handleShowModal,
  handleOffer,
  amount,
  type,
}) => {
  const { email, phoneNumber, displayName, name, surname } = auth.currentUser
  const doGetLawyerStripeAccountId = useGetLawyerStripeAccountId()

  const stripe = useStripe()
  const classes = useStyles()

  const elements = useElements()
  const [error, setError] = useState(null)
  const [cardComplete, setCardComplete] = useState(false)
  const [successPayment, setSuccessPayment] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [stripeAccountId, setStripeAccountId] = useState(null)
  const [billingDetails, setBillingDetails] = useState({
    email: email,
    phone: phoneNumber,
    name: displayName ? displayName : `${name} ${surname}`,
  })

  const getLawyerStripeData = async () => {
    const stripeData = await doGetLawyerStripeAccountId(offer.senderUid)
    setStripeAccountId(stripeData.account_id)
  }

  useEffect(() => {
    if (offer) {
      getLawyerStripeData()
    }
  }, [offer])

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!cardComplete) {
      elements.getElement('card').focus()
    }

    if (!stripe || !elements || processing || !cardComplete) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return
    }

    if (error) {
      elements.getElement('card').focus()
      return
    }

    if (cardComplete) {
      setProcessing(true)
    }

    const payload = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
      billing_details: billingDetails,
    })

    if (payload.error) {
      setError(payload.error)
      setProcessing(false)
    } else {
      setPaymentMethod(payload.paymentMethod)
    }
  }

  useEffect(() => {
    if (paymentMethod) {
      let paymentObject = {}
      if (type === 'directCharge') {
        paymentObject = {
          ...paymentMethod,
          amount: Math.round(calculateCCAfterFee(amount) * 100),
          offerId,
          type: 'directCharge',
        }
      } else {
        const fee = offer.amount * 0.12 * 100
        const taxes = (calculateCCAfterFee(offer.amount) - offer.amount) * 100

        paymentObject = {
          ...paymentMethod,
          amountWithoutTaxes: offer.amount * 100,
          amount: Math.round(calculateCCAfterFee(offer.amount) * 100),
          fee: Math.round(fee + taxes),
          customerId: stripeAccountId,
          offerId,
        }
      }

      axios
        .post(`${firebaseBaseURL}stripe/payment-intent`, paymentObject)
        .then((result) => {
          toast.success('Payment was successful!')
          handleOffer(result.data)
          handleShowModal(false)
        })
        .catch((err) => {
          if (
            err.response.data &&
            err.response.data.payment_intent &&
            err.response.data.payment_intent.last_payment_error
          ) {
            setError(err.response.data.payment_intent.last_payment_error)
          } else {
            setError('There was an error with the payment, please try again!')
          }
          setProcessing(false)
        })
    }
  }, [paymentMethod])

  const reset = () => {
    setError(null)
    setProcessing(false)
    setPaymentMethod(null)
    setBillingDetails({
      email: '',
      phone: '',
      name: '',
    })
  }

  return successPayment ? (
    <div className='Result'>
      <div className='ResultTitle' role='alert'>
        Payment successful
      </div>
      <div className='ResultMessage'>
        Thanks for trying Stripe Elements. No money was charged, but we
        generated a PaymentMethod: {paymentMethod.id}
      </div>
      <ResetButton onClick={reset} />
    </div>
  ) : (
    <form className='o-stripeCheckoutForm__form' onSubmit={handleSubmit}>
      {processing && (
        <div className='o-stripeCheckoutForm__formLoader'>
          <Loader />
        </div>
      )}
      <header>
        <b>Payment details</b>

        <button type='button' onClick={() => handleShowModal(false)}>
          <CloseIcon />
        </button>
      </header>
      <fieldset className='o-stripeCheckoutForm__formGroup'>
        <FormControl
          fullWidth
          variant='outlined'
          className={classes.formControl}
        >
          <TextField
            required
            className={classes.field}
            label='Name'
            variant='outlined'
            value={billingDetails.name}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, name: e.target.value })
            }}
          />
        </FormControl>
        <FormControl
          fullWidth
          variant='outlined'
          className={classes.formControl}
        >
          <TextField
            required
            className={classes.field}
            label='Email'
            variant='outlined'
            value={billingDetails.email}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, email: e.target.value })
            }}
          />
        </FormControl>

        <FormControl
          fullWidth
          variant='outlined'
          className={classes.formControl}
        >
          <TextField
            required
            className={classes.field}
            label='Phone'
            variant='outlined'
            value={billingDetails.phone}
            onChange={(e) => {
              setBillingDetails({ ...billingDetails, phone: e.target.value })
            }}
          />
        </FormControl>

        <FormControl
          fullWidth
          variant='outlined'
          className={classes.formControl}
        >
          <CardElement
            options={CARD_OPTIONS}
            onChange={(e) => {
              setError(e.error)
              setCardComplete(e.complete)
            }}
            className='o-stripeCheckoutForm__content--card'
          />
        </FormControl>
      </fieldset>

      {type !== 'directCharge' && (
        <div className='o-stripeCheckoutForm__offer'>
          <header>
            <span className='-badge'>New offer</span>
            <span>
              {expertise.find((exp) => exp.value === offer.for).label}
            </span>
            {offer.paymentType === 'noWinNoFee' ? (
              <b>{offer.rate}%</b>
            ) : (
              <b>{formatter.format(offer.amount)}</b>
            )}
          </header>
          <main>
            <p>{offer.description}</p>
            <strong>Offer details</strong>
            <ul>
              {offer.rate && offer.paymentType !== 'noWinNoFee' && (
                <li>
                  <span>Hourly Rate</span>
                  <span>{formatter.format(offer.rate)}</span>
                </li>
              )}
              {offer.paymentType === 'noWinNoFee' && (
                <li>
                  <span>Rate</span>
                  <span>{offer.rate}%</span>
                </li>
              )}
              {offer.estimatedHours && (
                <li>
                  <span>Estimated Hours</span>
                  <span>{offer.estimatedHours} hours</span>
                </li>
              )}
              <li>
                <span>Deadline</span>
                <span>
                  {moment(offer.deadline.toDate()).format('DD/MM/YYYY')}
                </span>
              </li>
              <li>
                <span>Transaction fee</span>
                <b>
                  {formatter.format(
                    calculateCCAfterFee(offer.amount) - offer.amount
                  )}
                </b>
              </li>
              <li>
                <span>Total</span>
                <b>{formatter.format(calculateCCAfterFee(offer.amount))}</b>
              </li>
            </ul>
          </main>
        </div>
      )}

      {error && (
        <FormControl
          fullWidth
          variant='outlined'
          className={classes.formControl}
        >
          <div className='o-stripeCheckoutForm__formError'>{error.message}</div>
        </FormControl>
      )}
      <div className='a-modal__actions'>
        <button
          type='button'
          className='a-modal__actions--cancelButton'
          onClick={() => handleShowModal(false)}
        >
          Cancel
        </button>
        <SubmitButton processing={processing} error={error} disabled={!stripe}>
          {type === 'directCharge'
            ? `Pay ${formatter.format(amount)}`
            : 'Confirm & Pay'}
        </SubmitButton>
      </div>
    </form>
  )
}

const StripeCheckoutForm = ({
  offerId,
  offer,
  handleShowModal,
  handleOffer,
  amount = null,
  type = null,
}) => {
  return (
    <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
      <div className='o-stripeCheckoutForm'>
        <div className='o-stripeCheckoutForm__content'>
          <CheckoutForm
            handleOffer={handleOffer}
            handleShowModal={handleShowModal}
            offerId={offerId}
            offer={offer}
            amount={amount}
            type={type}
          />
        </div>
      </div>
    </Elements>
  )
}
export default StripeCheckoutForm
